/* The middle column: whatever is selected, this is its whole surface.
 *
 * One pattern at every level — a form over the thing's own data, then its
 * children as a table, and a click on a row opens the child over this same
 * column. The breadcrumb at the top is the way back. Founding a child happens
 * here too, as a full form with every field the model has for that level.
 */

import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { can, type ContentRow } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { markdown } from "../../blocks/markdown";
import { GOAL_LEVELS, KIT_KINDS, type Localized, type Taxonomy } from "../../lib/types";
import { asPath, DRAFT, getAt, PUBLISHED, type BlockPath, type Ed, type Sel } from "./Content";
import { shortTitle } from "./ContentTree";
import { Fields, setAt } from "./fields";

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const cs = (v: unknown): string =>
  typeof v === "string" ? v : isObject(v) && typeof v["cs"] === "string" ? v["cs"] : "";
const en = (v: unknown): string =>
  isObject(v) && typeof v["en"] === "string" ? v["en"] : "";

const KIND_LABEL: Record<string, string> = {
  "vyklad": "Výklad", "hands-on": "Cvičení v hodině", "samostatna": "Samostatná práce",
  "tymova": "Týmová práce", "overeni": "Ověření", "samostudium": "Samostudium",
  "aplikace": "Aplikace", "audio-vyklad": "Audio výklad", "audio-shrnuti": "Audio shrnutí",
  "predani": "Předání kolegovi",
};
const LEVEL_LABEL: Record<string, string> = {
  zaklad: "Základní", bezny: "Očekávaná", rozsireny: "Rozšířená",
};

const docId = (path: string): string =>
  path.slice(path.lastIndexOf("/") + 1).replace(/\.json$/, "");

const taxName = (v: Localized | undefined, fallback: string): string =>
  typeof v === "string" ? v || fallback : v?.cs || fallback;

/** Whether a machine wrote the English at this address and nobody has read it.
 *  The marks carry the document's own addresses — see translate.ts, where they
 *  come out of the decomposition rather than out of a second mapping. */
const isMachine = (ed: Ed, path: BlockPath): boolean =>
  ed.marks.some((m) => m.at === asPath(path));

/** What a block says about itself in one line of a table. */
function blockPreview(block: unknown): string {
  if (!isObject(block)) return "";
  for (const key of ["title", "body", "q", "task", "brief", "caption", "app", "fig"]) {
    const line = cs(block[key]);
    if (line) return line;
  }
  return "";
}

/* ── the small vocabulary the views are written in ─────────────────────── */

function Table({ head, rows, onRow }: {
  head: ReactNode[]; rows: ReactNode[][]; onRow?: (i: number) => void;
}) {
  return (
    <div className="edx-tblwrap">
      <table className="edx-tbl">
        <thead><tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} onClick={onRow ? () => onRow(i) : undefined}
                className={onRow ? "is-row" : ""}>
              {r.map((c, j) => <td key={j}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Sec({ title, hint, action, children }: {
  title: string; hint?: string; action?: ReactNode; children?: ReactNode;
}) {
  return (
    <section className="edx-sec">
      <div className="edx-secbar">
        <h4>{title}</h4>
        {hint ? <span className="edx-hint">{hint}</span> : null}
        <span className="edx-grow" />
        {action}
      </div>
      {children}
    </section>
  );
}

/** One curated field: uncontrolled, saved as the author leaves it. The key on
 *  the wrapper re-seats it whenever the document is replaced under the form. */
function F({ ed, label, path, tall, num, hint, onSave }: {
  ed: Ed; label: string; path: BlockPath; tall?: boolean; num?: boolean; hint?: string;
  onSave?: (v: string) => void;
}) {
  const value = getAt(ed.parsed, path);
  const text = num ? String(typeof value === "number" ? value : "") : cs(value);
  const save = (raw: string) => {
    if (onSave) return onSave(raw);
    if (num) {
      const n = Number(raw);
      if (raw.trim() === "") ed.saveField(path, undefined);
      else if (!Number.isNaN(n)) ed.saveField(path, n);
      return;
    }
    saveLoc(ed, path, raw, "cs");
  };
  return (
    <div className="edx-fld" key={`${ed.ver}:${asPath(path)}`}>
      <label>{label} <code>{asPath(path)}</code></label>
      {tall
        ? <textarea defaultValue={text} onBlur={(e) => save(e.target.value)} />
        : <input type="text" defaultValue={text} onBlur={(e) => save(e.target.value)} />}
      {hint ? <p className="edx-fhint">{hint}</p> : null}
    </div>
  );
}

/**
 * A localized value, saved without ever losing its other half. A bare string
 * is shorthand for { cs }; adding English turns it into the pair, clearing
 * English removes only the `en` row.
 */
function saveLoc(ed: Ed, path: BlockPath, raw: string, lang: "cs" | "en") {
  const cur = getAt(ed.parsed, path);
  if (lang === "cs") {
    if (isObject(cur)) ed.saveField([...path, "cs"], raw);
    else ed.saveField(path, raw);
    return;
  }
  if (raw === "") {
    /* Clearing English replaces the pair with the bare Czech string: the
       canonical form writes cs-only that way, and a { cs } object left behind
       would not survive the model's round trip. */
    if (isObject(cur) && "en" in cur) ed.saveField(path, cs(cur));
    return;
  }
  if (isObject(cur)) ed.saveField([...path, "en"], raw);
  else ed.saveField(path, { cs: cs(cur), en: raw });
}

function FLoc({ ed, labelCs, labelEn, path, tall }: {
  ed: Ed; labelCs: string; labelEn: string; path: BlockPath; tall?: boolean;
}) {
  const { ui } = useI18n();
  const cur = getAt(ed.parsed, path);
  const key = `${ed.ver}:${asPath(path)}`;
  /* Whether a machine wrote this English and nobody has read it. The mark is a
     row of the model, addressed by owner and path, so the field asks by the
     tail of its own address — `kit[2].claim` is the kit item's `claim`. */
  const machine = isMachine(ed, path);
  const half = (lang: "cs" | "en", label: string, value: string) => (
    <div className="edx-fld" key={`${key}:${lang}`}>
      <label>{label} <code>{asPath(path)}.{lang}</code>
        {lang === "en" && machine
          ? <em className="edx-machine">{ui("content.machine")}</em> : null}</label>
      {tall
        ? <textarea defaultValue={value}
                    onBlur={(e) => saveLoc(ed, path, e.target.value, lang)} />
        : <input type="text" defaultValue={value}
                 onBlur={(e) => saveLoc(ed, path, e.target.value, lang)} />}
    </div>
  );
  return <>{half("cs", labelCs, cs(cur))}{half("en", labelEn, en(cur))}</>;
}

function statePill(ui: (k: string) => string, status: string) {
  return <span className={"edx-pill " + (status === DRAFT ? "draft" : "pub")}>
    {ui(status === DRAFT ? "content.draft" : "content.published")}
  </span>;
}

function readyChip(ui: (k: string) => string, item: Record<string, unknown> | undefined) {
  if (!item) return <span className="edx-chip off">{ui("content.notCreated")}</span>;
  if ("absent" in item) return <span className="edx-chip off">{ui("content.absentChip")}</span>;
  const ready = item["ready"];
  if (isObject(ready) && typeof ready["as"] === "string") {
    const key = { blocks: "content.readyBlocks", app: "content.readyApp", audio: "content.readyAudio" }[ready["as"]];
    if (key) return <span className="edx-chip done">{ui(key)}</span>;
  }
  return <span className="edx-chip warn">{ui("content.promptOnly")}</span>;
}

function levelDots(levels: unknown) {
  const lv = Array.isArray(levels) ? levels : [];
  return (
    <span className="edx-lvls">
      {GOAL_LEVELS.map((l) => (
        <i key={l} className={lv.includes(l) ? "on" : ""} title={LEVEL_LABEL[l]}>
          {LEVEL_LABEL[l]![0]}
        </i>
      ))}
    </span>
  );
}

/* ── breadcrumbs ───────────────────────────────────────────────────────── */

function Crumbs({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const parts: [string, Sel | null][] = [];
  const s = ed.sel;
  if (s.taxJson) {
    parts.push([ui("content.taxonomyJson"), null]);
  } else if (s.apps || s.doc?.startsWith("apps/")) {
    parts.push([ui("content.apps"), { apps: true }]);
  } else if (s.pages) {
    parts.push([ui("content.pages"), { pages: true }]);
  } else {
    parts.push([ui("content.schools"), {}]);
    if (s.school) {
      parts.push([taxName(ed.tax?.strands[s.school]?.short, s.school), { school: s.school }]);
    }
    if (s.subject) {
      parts.push([taxName(ed.tax?.subjects[s.subject]?.name, s.subject),
        { school: s.school, subject: s.subject }]);
    }
  }
  if (s.doc && s.doc.startsWith("docs/")) {
    const row = ed.rows.find((r) => r.path === s.doc);
    parts.push([row ? shortTitle(row) : docId(s.doc),
      { school: s.school, subject: s.subject, pages: s.pages, doc: s.doc }]);
  }
  if (s.sub) {
    const label = s.sub.kind === "kit"
      ? KIND_LABEL[String(getAt(ed.parsed, ["kit", s.sub.i, "kind"]))] ?? ui("content.kit")
      : s.sub.kind === "goal"
        ? `${ui("content.goal")} — ${LEVEL_LABEL[String(getAt(ed.parsed, ["goal", "levels", s.sub.i, "id"]))] ?? ""}`
        : s.sub.kind === "part" ? `${ui("content.parts")} ${s.sub.i + 1}`
          : `Blok`;
    parts.push([label, null]);
  }
  if (s.creating) {
    parts.push([{ school: ui("content.newSchool"), subject: ui("content.newSubject"),
      lesson: ui("content.newLesson"), page: ui("content.newPage") }[s.creating], null]);
  }
  return (
    <div className="edx-bc">
      {parts.map(([label, target], i) =>
        i === parts.length - 1 || target === null
          ? <b key={i}>{label}</b>
          : <span key={i}>
              <button type="button" className="plain" onClick={() => ed.go(target)}>{label}</button>
              <span aria-hidden="true"> › </span>
            </span>)}
    </div>
  );
}

/* ── founding forms: every field the level has ─────────────────────────── */

function NewForm({ ed, what }: { ed: Ed; what: NonNullable<Sel["creating"]> }) {
  const { ui, r } = useI18n();
  const back = () => ed.go({ ...ed.sel, creating: undefined });
  const areas = Object.entries(ed.tax?.areas ?? {});
  const strand = ed.sel.school ? ed.tax?.strands[ed.sel.school] : undefined;

  return (
    <form className="edx-form" onSubmit={(e) => {
      e.preventDefault();
      void ed.create(what, Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>);
    }}>
      {what === "school" ? <>
        <div className="edx-fld wide"><label>{ui("content.name")}</label>
          <input name="name" required /></div>
        <div className="edx-fld"><label>{ui("content.nameEn")}</label><input name="nameEn" /></div>
        <div className="edx-fld"><label>{ui("content.short")}</label><input name="short" /></div>
        <div className="edx-fld"><label>{ui("content.id")}</label>
          <input name="id" required pattern="[a-z][a-z0-9\-]*" /></div>
        <div className="edx-fld"><label>{ui("content.schoolType")}</label>
          <select name="schoolType">
            <option value="zs">{ui("content.zs")}</option>
            <option value="ss">{ui("content.ss")}</option>
          </select></div>
        <div className="edx-fld"><label>{ui("content.yearFrom")}</label>
          <input name="yearFrom" type="number" min="1" max="9" defaultValue="1" /></div>
        <div className="edx-fld"><label>{ui("content.yearTo")}</label>
          <input name="yearTo" type="number" min="1" max="9" defaultValue="4" /></div>
        <div className="edx-fld wide"><label>{ui("content.who")}</label>
          <textarea name="who" /></div>
      </> : null}

      {what === "subject" ? <>
        <div className="edx-fld wide"><label>{ui("content.name")}</label>
          <input name="name" required /></div>
        <div className="edx-fld"><label>{ui("content.nameEn")}</label><input name="nameEn" /></div>
        <div className="edx-fld"><label>{ui("content.id")}</label>
          <input name="id" required pattern="[a-z][a-z0-9\-]*" /></div>
        <div className="edx-fld"><label>{ui("content.area")}</label>
          <select name="area">
            {areas.map(([id, label]) => <option key={id} value={id}>{r(label).text || id}</option>)}
          </select></div>
      </> : null}

      {what === "lesson" ? <>
        <div className="edx-fld wide"><label>{ui("content.titleCs")}</label>
          <input name="title" required /></div>
        <div className="edx-fld wide"><label>{ui("content.titleEn")}</label>
          <input name="titleEn" /></div>
        <div className="edx-fld wide"><label>{ui("content.claim")}</label>
          <textarea name="claim" /></div>
        <div className="edx-fld"><label>{ui("content.id")}</label>
          <input name="id" required pattern="[a-z][a-z0-9\-]*" /></div>
        <div className="edx-fld"><label>{ui("content.year")}</label>
          <select name="year" defaultValue={String(strand?.years[0] ?? 1)}>
            {(strand?.years ?? [1]).map((y) => <option key={y} value={y}>{y}.</option>)}
          </select></div>
        <div className="edx-fld"><label>{ui("content.grade")}</label><input name="grade" /></div>
      </> : null}

      {what === "page" ? <>
        <div className="edx-fld wide"><label>{ui("content.titleCs")}</label>
          <input name="title" required /></div>
        <div className="edx-fld"><label>{ui("content.id")}</label>
          <input name="id" required pattern="[a-z][a-z0-9\-]*" /></div>
      </> : null}

      <p className="edx-fhint wide">{ui("content.idHint")}</p>
      <div className="edx-formfoot wide">
        <button type="submit" className="edx-act primary edx-ctl">
          {what === "lesson" || what === "page" ? ui("content.createDraft") : ui("content.create")}
        </button>
        <button type="button" className="plain edx-cancel" onClick={back}>{ui("content.cancel")}</button>
      </div>
    </form>
  );
}

/* ── level views ───────────────────────────────────────────────────────── */

function RootView({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const lessons = ed.rows.filter((r) => r.meta?.type === "lesson");
  const ids = ed.tax?.strandIds ?? [];
  return (
    <>
      <div className="edx-bar">
        <h2 className="edx-title">{ui("content.schools")}</h2>
        <span className="edx-grow" />
        <button type="button" className="edx-act primary edx-ctl"
                onClick={() => ed.go({ creating: "school" })}>+ {ui("content.newSchool")}</button>
      </div>
      <Table
        head={[ui("content.school"), ui("content.schoolType"), ui("content.year"),
          ui("content.subjects"), ui("content.countLessons")]}
        rows={ids.map((sid) => {
          const strand = ed.tax?.strands[sid];
          const mine = lessons.filter((r) => r.meta?.strand?.includes(sid));
          const subjects = new Set(mine.map((r) => r.meta?.subject));
          const years = strand?.years ?? [];
          return [
            <b>{taxName(strand?.name, sid)}</b>,
            strand?.schoolType === "ss" ? ui("content.ss") : ui("content.zs"),
            years.length ? `${years[0]}.–${years[years.length - 1]}.` : "—",
            String(subjects.size),
            String(mine.length),
          ];
        })}
        onRow={(i) => ed.go({ school: ids[i] })}
      />
    </>
  );
}

function SchoolView({ ed }: { ed: Ed }) {
  const { ui, r } = useI18n();
  const sid = ed.sel.school!;
  const strand = ed.tax?.strands[sid];
  const lessons = ed.rows.filter((r) => r.meta?.type === "lesson" && r.meta.strand?.includes(sid));
  const subjects = Object.values(ed.tax?.subjects ?? {});

  /* A field of the strand: edited here, stored in the taxonomy — the school
     IS its strand, there is no second record of it anywhere. */
  const tf = (label: string, key: string, value: string, apply: (t: Taxonomy, v: string) => void,
              wide = false, tall = false) => (
    <div className={"edx-fld" + (wide ? " wide" : "")} key={`${sid}:${key}:${value}`}>
      <label>{label} <code>{`strands.${sid}.${key}`}</code></label>
      {tall
        ? <textarea defaultValue={value}
                    onBlur={(e) => void ed.saveTax((t) => { apply(t, e.target.value); return t; })} />
        : <input type="text" defaultValue={value}
                 onBlur={(e) => void ed.saveTax((t) => { apply(t, e.target.value); return t; })} />}
    </div>
  );
  const loc = (v: Localized | undefined): { cs: string; en?: string } =>
    typeof v === "string" ? { cs: v } : { cs: v?.cs ?? "", ...(v?.en ? { en: v.en } : {}) };

  if (!strand) return null;
  return (
    <>
      <div className="edx-bar"><h2 className="edx-title">{taxName(strand.name, sid)}</h2></div>
      <div className="edx-form">
        {tf(ui("content.name"), "name.cs", loc(strand.name).cs,
          (t, v) => { t.strands[sid]!.name = { ...loc(t.strands[sid]!.name), cs: v }; }, true)}
        {tf(ui("content.nameEn"), "name.en", loc(strand.name).en ?? "",
          (t, v) => {
            const cur = loc(t.strands[sid]!.name);
            t.strands[sid]!.name = v ? { ...cur, en: v } : { cs: cur.cs };
          })}
        {tf(ui("content.short"), "short.cs", loc(strand.short).cs,
          (t, v) => { t.strands[sid]!.short = { ...loc(t.strands[sid]!.short), cs: v }; })}
        <div className="edx-fld" key={`${sid}:type:${strand.schoolType}`}>
          <label>{ui("content.schoolType")} <code>{`strands.${sid}.schoolType`}</code></label>
          <select defaultValue={strand.schoolType}
                  onChange={(e) => void ed.saveTax((t) => {
                    t.strands[sid]!.schoolType = e.target.value === "ss" ? "ss" : "zs";
                    return t;
                  })}>
            <option value="zs">{ui("content.zs")}</option>
            <option value="ss">{ui("content.ss")}</option>
          </select>
        </div>
        <div className="edx-fld" key={`${sid}:years:${strand.years.join()}`}>
          <label>{ui("content.year")} <code>{`strands.${sid}.years`}</code></label>
          <div className="edx-inline">
            <input type="number" min={1} max={9} defaultValue={strand.years[0]}
                   aria-label={ui("content.yearFrom")}
                   onBlur={(e) => void ed.saveTax((t) => {
                     const to = t.strands[sid]!.years.at(-1) ?? 9;
                     const from = Math.min(Math.max(1, Number(e.target.value) || 1), to);
                     t.strands[sid]!.years =
                       Array.from({ length: to - from + 1 }, (_, i) => from + i);
                     return t;
                   })} />
            <span>–</span>
            <input type="number" min={1} max={9} defaultValue={strand.years.at(-1)}
                   aria-label={ui("content.yearTo")}
                   onBlur={(e) => void ed.saveTax((t) => {
                     const from = t.strands[sid]!.years[0] ?? 1;
                     const to = Math.max(from, Math.min(9, Number(e.target.value) || from));
                     t.strands[sid]!.years =
                       Array.from({ length: to - from + 1 }, (_, i) => from + i);
                     return t;
                   })} />
          </div>
        </div>
        {tf(ui("content.who"), "who.cs", loc(strand.who).cs,
          (t, v) => { t.strands[sid]!.who = { ...loc(t.strands[sid]!.who), cs: v }; }, true, true)}
      </div>

      <Sec title={ui("content.subjects")} hint={ui("content.subjectEverywhere")}
           action={<button type="button" className="edx-add edx-ctl"
                           onClick={() => ed.go({ school: sid, creating: "subject" })}>
             + {ui("content.newSubject")}</button>}>
        <Table
          head={[ui("content.subject"), ui("content.area"), ui("content.countLessons")]}
          rows={subjects.map((sub) => {
            const inHere = lessons.filter((l) => l.meta?.subject === sub.id).length;
            const area = ed.tax?.areas[sub.area];
            return [
              <b>{taxName(sub.name, sub.id)}</b>,
              area ? r(area).text : sub.area,
              inHere ? String(inHere) : <span className="edx-mut">0</span>,
            ];
          })}
          onRow={(i) => ed.go({ school: sid, subject: subjects[i]!.id })}
        />
      </Sec>
    </>
  );
}

function SubjectView({ ed }: { ed: Ed }) {
  const { ui, r } = useI18n();
  const sid = ed.sel.subject!;
  const subject = ed.tax?.subjects[sid];
  const areas = Object.entries(ed.tax?.areas ?? {});
  /* All its lessons, wherever they run: the subject is shared by the schools,
     so the strand is a column of the table rather than a filter on it. */
  const lessons = ed.rows
    .filter((row) => row.meta?.type === "lesson" && row.meta.subject === sid)
    .sort((a, b) => (a.meta?.year ?? 0) - (b.meta?.year ?? 0));

  const loc = (v: Localized | undefined): { cs: string; en?: string } =>
    typeof v === "string" ? { cs: v } : { cs: v?.cs ?? "", ...(v?.en ? { en: v.en } : {}) };

  if (!subject) return null;
  return (
    <>
      <div className="edx-bar"><h2 className="edx-title">{taxName(subject.name, sid)}</h2></div>
      <div className="edx-form">
        <div className="edx-fld" key={`${sid}:name:${loc(subject.name).cs}`}>
          <label>{ui("content.name")} <code>{`subjects.${sid}.name.cs`}</code></label>
          <input type="text" defaultValue={loc(subject.name).cs}
                 onBlur={(e) => void ed.saveTax((t) => {
                   t.subjects[sid]!.name = { ...loc(t.subjects[sid]!.name), cs: e.target.value };
                   return t;
                 })} />
        </div>
        <div className="edx-fld" key={`${sid}:nameEn:${loc(subject.name).en ?? ""}`}>
          <label>{ui("content.nameEn")} <code>{`subjects.${sid}.name.en`}</code></label>
          <input type="text" defaultValue={loc(subject.name).en ?? ""}
                 onBlur={(e) => void ed.saveTax((t) => {
                   const cur = loc(t.subjects[sid]!.name);
                   t.subjects[sid]!.name = e.target.value
                     ? { ...cur, en: e.target.value } : { cs: cur.cs };
                   return t;
                 })} />
        </div>
        <div className="edx-fld" key={`${sid}:area:${subject.area}`}>
          <label>{ui("content.area")} <code>{`subjects.${sid}.area`}</code></label>
          <select defaultValue={subject.area}
                  onChange={(e) => void ed.saveTax((t) => {
                    t.subjects[sid]!.area = e.target.value;
                    return t;
                  })}>
            {areas.map(([id, label]) => <option key={id} value={id}>{r(label).text || id}</option>)}
          </select>
        </div>
      </div>

      <Sec title={ui("content.lessons")}
           action={ed.sel.school
             ? <button type="button" className="edx-add edx-ctl"
                       onClick={() => ed.go({ ...ed.sel, creating: "lesson" })}>
                 + {ui("content.newLesson")}</button>
             : undefined}>
        {lessons.length ? (
          <Table
            head={[ui("content.year"), ui("content.lessons"), ui("content.school"),
              ui("content.state"), ui("content.filledCol"), ui("content.updatedCol")]}
            rows={lessons.map((row) => [
              <span className="edx-mut">{row.meta?.year != null ? `${row.meta.year}.` : "—"}</span>,
              <b>{shortTitle(row)}</b>,
              <span className="edx-mut">
                {(row.meta?.strand ?? []).map((s) => taxName(ed.tax?.strands[s]?.short, s)).join(", ")}
              </span>,
              statePill(ui, row.status),
              <span className="edx-mut">—</span>,
              <span className="edx-mut">
                {new Date(row.updated_at).toLocaleDateString()} · {row.updated_by ?? ui("content.never")}
              </span>,
            ])}
            onRow={(i) => ed.go({ ...ed.sel, doc: lessons[i]!.path })}
          />
        ) : <p className="edx-empty">{ui("content.emptyLessons")}</p>}
      </Sec>
    </>
  );
}

function PagesView({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const pages = ed.rows.filter((r) => r.kind === "doc" && r.meta?.type !== "lesson");
  return (
    <>
      <div className="edx-bar">
        <h2 className="edx-title">{ui("content.pages")}</h2>
        <span className="edx-grow" />
        <button type="button" className="edx-act primary edx-ctl"
                onClick={() => ed.go({ pages: true, creating: "page" })}>+ {ui("content.newPage")}</button>
      </div>
      <Table
        head={[ui("content.pages"), ui("content.pathCol"), ui("content.state"), ui("content.updatedCol")]}
        rows={pages.map((row) => [
          <b>{row.meta?.title || row.path}</b>,
          <code>/p/{docId(row.path)}</code>,
          statePill(ui, row.status),
          <span className="edx-mut">
            {new Date(row.updated_at).toLocaleDateString()} · {row.updated_by ?? ui("content.never")}
          </span>,
        ])}
        onRow={(i) => ed.go({ pages: true, doc: pages[i]!.path })}
      />
    </>
  );
}

function AppsView({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const apps = ed.rows.filter((r) => r.kind === "app");
  return (
    <>
      <div className="edx-bar"><h2 className="edx-title">{ui("content.apps")}</h2></div>
      <Table
        head={[ui("content.pathCol"), ui("content.state"), ui("content.sizeCol"), ui("content.updatedCol")]}
        rows={apps.map((row) => [
          <code>{row.path}</code>,
          statePill(ui, row.status),
          <span className="edx-mut">{Math.round(row.size / 1024)} kB</span>,
          <span className="edx-mut">{new Date(row.updated_at).toLocaleDateString()}</span>,
        ])}
        onRow={(i) => ed.go({ apps: true, doc: apps[i]!.path })}
      />
    </>
  );
}

/** An application or the taxonomy: one text, edited whole. */
function TextView({ ed, row }: { ed: Ed; row: ContentRow | undefined }) {
  const { ui } = useI18n();
  return (
    <>
      <div className="edx-bar">
        <h2 className="edx-title"><code>{ed.sel.doc}</code></h2>
        {row ? statePill(ui, row.status) : null}
        <span className="edx-grow" />
        {ed.dirty ? <em className="ed-dirty">{ui("content.unsaved")}</em> : null}
        <button type="button" className="edx-act primary edx-ctl" disabled={ed.busy || !ed.dirty}
                onClick={() => void ed.saveWhole()}>{ui("content.save")}</button>
        {can(ed.me, "content.delete") && ed.sel.doc !== "taxonomy.json" ? (
          <button type="button" className="adm-link" disabled={ed.busy}
                  onClick={() => void ed.removeDoc()}>{ui("content.delete")}</button>
        ) : null}
      </div>
      <textarea className="ed-json" spellCheck={false} value={ed.text}
                onChange={(e) => ed.setText(e.target.value)} />
    </>
  );
}

/* ── the document and what is inside it ────────────────────────────────── */

function DocBar({ ed, row }: { ed: Ed; row: ContentRow | undefined }) {
  const { ui } = useI18n();
  const title = cs(ed.parsed?.["title"]) || (ed.sel.doc ? docId(ed.sel.doc) : "");
  const status = row?.status ?? PUBLISHED;
  return (
    <div className="edx-bar">
      <h2 className="edx-title">{title}</h2>
      {statePill(ui, status)}
      <span className="edx-grow" />
      {ed.marks.length ? (
        <button type="button" className="edx-act edx-ctl" disabled={ed.busy}
                onClick={() => void ed.confirmMarks()}
                title={ui("content.confirmAllHint")}>
          {ed.marks.length} {ui("content.unread")} · {ui("content.confirmAll")}
        </button>
      ) : null}
      <button type="button" className="edx-act edx-ctl" disabled={ed.busy}
              onClick={() => void ed.translate()}>{ui("content.translate")}</button>
      <button type="button" className="edx-ghost edx-ctl" aria-pressed={ed.jsonMode}
              onClick={() => ed.setJsonMode(!ed.jsonMode)}>{ui("content.json")}</button>
      <button type="button" className="edx-act primary edx-ctl" disabled={ed.busy}
              onClick={() => void ed.saveWhole(status === DRAFT ? PUBLISHED : DRAFT)}>
        {status === DRAFT ? ui("content.publish") : ui("content.unpublish")}
      </button>
      {can(ed.me, "content.delete") ? (
        <button type="button" className="adm-link" disabled={ed.busy}
                onClick={() => void ed.removeDoc()}>{ui("content.delete")}</button>
      ) : null}
    </div>
  );
}

function BlocksTable({ ed, base, blocks }: { ed: Ed; base: BlockPath; blocks: unknown[] }) {
  const { ui } = useI18n();
  return (
    <Table
      head={["#", ui("content.kindCol"), ui("content.blockContent")]}
      rows={blocks.map((b, i) => [
        <span className="edx-mut">{i + 1}</span>,
        <code>{isObject(b) ? String(b["k"] ?? "") : ""}</code>,
        <span className="edx-prev">{blockPreview(b)}</span>,
      ])}
      onRow={(i) => ed.go({ ...ed.sel, sub: { kind: "block", path: [...base, i] } })}
    />
  );
}

function DocView({ ed, row }: { ed: Ed; row: ContentRow | undefined }) {
  const { ui } = useI18n();
  const doc = ed.parsed;
  if (!doc) return <p className="adm-warn">{ui("content.invalidJson")}</p>;
  const lesson = doc["type"] === "lesson";
  const meta = isObject(doc["meta"]) ? doc["meta"] : {};
  const goal = isObject(doc["goal"]) && Array.isArray(doc["goal"]["levels"])
    ? doc["goal"]["levels"] : [];
  const kit = Array.isArray(doc["kit"]) ? doc["kit"] : [];
  const parts = Array.isArray(doc["parts"]) ? doc["parts"] : [];
  const blocks = Array.isArray(doc["blocks"]) ? doc["blocks"] : [];
  const strandIds = ed.tax?.strandIds ?? [];
  const subjects = Object.values(ed.tax?.subjects ?? {});

  const kitIndex = new Map(kit.map((item, i) =>
    [isObject(item) ? String(item["kind"]) : "", i] as const));

  /* Founding a kit item is one edit: the smallest present item the schema
     accepts, appended to the array — the author fills it in its own view. */
  const createItem = (kind: string) => {
    ed.saveField(["kit", kit.length],
      { kind, title: "", claim: "", levels: ["bezny"], prompt: { core: "" } });
    ed.go({ ...ed.sel, sub: { kind: "kit", i: kit.length } });
  };

  return (
    <>
      <DocBar ed={ed} row={row} />
      {ed.jsonMode ? (
        <>
          {ed.dirty ? <em className="ed-dirty">{ui("content.unsaved")}</em> : null}
          <button type="button" className="edx-act primary edx-ctl" style={{ float: "right" }}
                  disabled={ed.busy || !ed.dirty}
                  onClick={() => void ed.saveWhole()}>{ui("content.save")}</button>
          <textarea className="ed-json" spellCheck={false} value={ed.text}
                    onChange={(e) => ed.setText(e.target.value)} />
        </>
      ) : (
        <>
          <div className="edx-form">
            <FLoc ed={ed} labelCs={ui("content.titleCs")} labelEn={ui("content.titleEn")}
                  path={["title"]} />
            <div className="wide">
              <FLoc ed={ed} labelCs={ui("content.claim")} labelEn={`${ui("content.claim")} · EN`}
                    path={["claim"]} tall />
            </div>
            {lesson ? <>
              <div className="edx-fld" key={`${ed.ver}:subject`}>
                <label>{ui("content.subject")} <code>meta.subject</code></label>
                <select defaultValue={String(meta["subject"] ?? "")}
                        onChange={(e) => ed.saveField(["meta", "subject"], e.target.value)}>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{taxName(s.name, s.id)}</option>
                  ))}
                </select>
              </div>
              <F ed={ed} label={ui("content.year")} path={["meta", "year"]} num />
              <F ed={ed} label={ui("content.grade")} path={["meta", "grade"]} />
              <div className="edx-fld" key={`${ed.ver}:strand`}>
                <label>{ui("content.strands")} <code>meta.strand</code></label>
                <div className="edx-checks">
                  {strandIds.map((sid) => {
                    const cur = Array.isArray(meta["strand"]) ? meta["strand"] as string[] : [];
                    return (
                      <label key={sid}>
                        <input type="checkbox" checked={cur.includes(sid)}
                               onChange={(e) => {
                                 const next = e.target.checked
                                   ? strandIds.filter((s) => cur.includes(s) || s === sid)
                                   : cur.filter((s) => s !== sid);
                                 ed.saveField(["meta", "strand"], next);
                               }} />
                        {" "}{taxName(ed.tax?.strands[sid]?.short, sid)}
                      </label>
                    );
                  })}
                </div>
              </div>
            </> : null}
          </div>

          {lesson ? (
            <>
              <Sec title={ui("content.goal")} hint={ui("content.goalHint")}>
                <Table
                  head={[ui("content.level"), ui("content.who"), ui("content.can"), ui("content.proof")]}
                  rows={goal.map((lv) => {
                    const g = isObject(lv) ? lv : {};
                    return [
                      <b>{LEVEL_LABEL[String(g["id"])] ?? String(g["id"])}</b>,
                      cs(g["who"]) || <span className="edx-faded">…</span>,
                      <span className="edx-serif">{cs(g["can"])
                        || <span className="edx-faded">{ui("content.noTitle")}</span>}</span>,
                      <span className="edx-mut">{cs(g["proof"])}</span>,
                    ];
                  })}
                  onRow={(i) => ed.go({ ...ed.sel, sub: { kind: "goal", i } })}
                />
              </Sec>
              <Sec title={ui("content.kit")} hint={ui("content.kitHint")}>
                <Table
                  head={["#", ui("content.kindCol"), ui("content.name"), ui("content.minutes"),
                    ui("content.goal"), ui("content.state")]}
                  rows={KIT_KINDS.map((kind, n) => {
                    const i = kitIndex.get(kind);
                    const item = i !== undefined && isObject(kit[i]) ? kit[i] : undefined;
                    return [
                      <span className="edx-mut">{n + 1}</span>,
                      <span className="edx-mut">{KIND_LABEL[kind]}</span>,
                      item && !("absent" in item)
                        ? (cs(item["title"]) ? <b>{cs(item["title"])}</b>
                          : <span className="edx-faded">{ui("content.noTitle")}</span>)
                        : <span className="edx-faded">{item ? cs(item["absent"]) : "—"}</span>,
                      <span className="edx-mut">
                        {item && typeof item["minutes"] === "number" ? `${item["minutes"]}′` : ""}
                      </span>,
                      item && !("absent" in item) ? levelDots(item["levels"]) : "",
                      readyChip(ui, item),
                    ];
                  })}
                  onRow={(n) => {
                    const kind = KIT_KINDS[n]!;
                    const i = kitIndex.get(kind);
                    if (i !== undefined) ed.go({ ...ed.sel, sub: { kind: "kit", i } });
                    else createItem(kind);
                  }}
                />
              </Sec>
            </>
          ) : (
            <>
              {parts.length ? (
                <Sec title={ui("content.parts")}>
                  <Table
                    head={["#", ui("content.id"), ui("content.blocksSec")]}
                    rows={parts.map((p, i) => [
                      <span className="edx-mut">{i + 1}</span>,
                      <code>{isObject(p) ? String(p["id"] ?? i) : ""}</code>,
                      <span className="edx-mut">
                        {isObject(p) && Array.isArray(p["blocks"]) ? p["blocks"].length : 0}
                      </span>,
                    ])}
                    onRow={(i) => ed.go({ ...ed.sel, sub: { kind: "part", i } })}
                  />
                </Sec>
              ) : null}
              <Sec title={ui("content.blocksSec")}
                   action={<button type="button" className="edx-add edx-ctl"
                                   onClick={() => {
                                     ed.saveField(["blocks", blocks.length], { k: "text", body: "" });
                                     ed.go({ ...ed.sel, sub: { kind: "block", path: ["blocks", blocks.length] } });
                                   }}>+ {ui("content.addBlock")}</button>}>
                <BlocksTable ed={ed} base={["blocks"]} blocks={blocks} />
              </Sec>
            </>
          )}
        </>
      )}
    </>
  );
}

function GoalView({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const i = ed.sel.sub?.kind === "goal" ? ed.sel.sub.i : 0;
  const level = getAt(ed.parsed, ["goal", "levels", i]);
  if (!isObject(level)) return null;
  return (
    <>
      <div className="edx-bar">
        <h2 className="edx-title">
          {ui("content.goal")} — {LEVEL_LABEL[String(level["id"])] ?? String(level["id"])}
        </h2>
      </div>
      <div className="edx-form">
        <F ed={ed} label={ui("content.who")} path={["goal", "levels", i, "who"]} tall
           hint="Čte jen vyučující; na projektor se nedostane." />
        <div className="wide">
          <F ed={ed} label={ui("content.can")} path={["goal", "levels", i, "can"]} tall
             hint="Pozorovatelné sloveso s předmětem a podmínkou — ne „chápe“, „umí“, „zná“." />
        </div>
        <div className="wide">
          <F ed={ed} label={ui("content.proof")} path={["goal", "levels", i, "proof"]} tall
             hint="Měřítko s množstvím: kolik z kolika, do kdy, s čím u ruky." />
        </div>
      </div>
    </>
  );
}

function KitView({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const [tab, setTab] = useState<"core" | "claude" | "gpt" | "gemini">("core");
  const i = ed.sel.sub?.kind === "kit" ? ed.sel.sub.i : 0;
  const item = getAt(ed.parsed, ["kit", i]);
  if (!isObject(item)) return null;
  const kind = String(item["kind"] ?? "");
  const absent = "absent" in item;
  const ready = isObject(item["ready"]) ? item["ready"] : null;
  const readyAs = ready ? String(ready["as"] ?? "") : null;
  const levels = Array.isArray(item["levels"]) ? item["levels"] as string[] : [];
  const prompt = isObject(item["prompt"]) ? item["prompt"] : {};
  const lessonId = ed.sel.doc ? docId(ed.sel.doc) : "";

  return (
    <>
      <div className="edx-bar">
        <h2 className="edx-title">{KIND_LABEL[kind] ?? kind}</h2>
        {readyChip(ui, item)}
      </div>

      {absent ? (
        <div className="edx-form">
          <F ed={ed} label={ui("content.absentWhy")} path={["kit", i, "absent"]} tall />
          <div className="edx-formfoot wide">
            <button type="button" className="edx-act edx-ctl"
                    onClick={() => ed.saveField(["kit", i],
                      { kind, title: "", claim: "", levels: ["bezny"], prompt: { core: "" } })}>
              {ui("content.makeAnyway")}
            </button>
          </div>
        </div>
      ) : (
        <div className="edx-form">
          <FLoc ed={ed} labelCs={`${ui("content.name")} · cs`} labelEn={`${ui("content.name")} · en`}
                path={["kit", i, "title"]} />
          <div className="wide">
            <FLoc ed={ed} labelCs={ui("content.claim")} labelEn={`${ui("content.claim")} · EN`}
                  path={["kit", i, "claim"]} tall />
          </div>
          <F ed={ed} label={ui("content.minutes")} path={["kit", i, "minutes"]} num />
          <div className="edx-fld" key={`${ed.ver}:lv:${i}:${levels.join()}`}>
            <label>{ui("content.levelsField")} <code>{`kit[${i}].levels`}</code></label>
            <div className="edx-checks">
              {GOAL_LEVELS.map((l) => (
                <label key={l}>
                  <input type="checkbox" checked={levels.includes(l)}
                         onChange={(e) => {
                           const next = e.target.checked
                             ? GOAL_LEVELS.filter((x) => levels.includes(x) || x === l)
                             : levels.filter((x) => x !== l);
                           ed.saveField(["kit", i, "levels"], next);
                         }} />
                  {" "}{LEVEL_LABEL[l]}
                </label>
              ))}
            </div>
          </div>
          <div className="edx-fld" key={`${ed.ver}:print:${i}`}>
            <label>{ui("content.print")} <code>{`kit[${i}].print`}</code></label>
            <div className="edx-checks">
              <label>
                <input type="checkbox" checked={item["print"] === true}
                       onChange={(e) => ed.saveField(["kit", i, "print"],
                         e.target.checked ? true : undefined)} />
                {" "}A4
              </label>
            </div>
          </div>

          <div className="edx-fld wide" key={`${ed.ver}:prompt:${i}:${tab}`}>
            <label>{ui("content.prompt")} <code>{`kit[${i}].prompt.${tab}`}</code></label>
            <div className="edx-ptabs">
              {(["core", "claude", "gpt", "gemini"] as const).map((m) => (
                <button key={m} type="button" className={"edx-ptab edx-ctl" + (tab === m ? " on" : "")}
                        onClick={() => setTab(m)}>
                  {m === "core" ? ui("content.promptCoreTab") : m}
                  {m !== "core" && cs(prompt[m]) ? " ●" : ""}
                </button>
              ))}
            </div>
            <textarea defaultValue={cs(prompt[tab])}
                      onBlur={(e) => {
                        const v = e.target.value;
                        const path: BlockPath = ["kit", i, "prompt", tab];
                        if (v === "" && tab !== "core") ed.saveField(path, undefined);
                        else if (isObject(prompt[tab])) ed.saveField([...path, "cs"], v);
                        else ed.saveField(path, v);
                      }} />
          </div>
          <F ed={ed} label={ui("content.promptLabel")} path={["kit", i, "prompt", "label"]}
             onSave={(v) => ed.saveField(["kit", i, "prompt", "label"], v === "" ? undefined : v)} />

          {readyAs === "audio" ? (
            <>
              <div className="wide">
                <F ed={ed} label={ui("content.script")} path={["kit", i, "ready", "script"]} tall />
              </div>
              {/* The recording, where there is one: an author who has just had
                  a script read aloud wants to hear it before a room does. */}
              {typeof ready?.["audio"] === "string" ? (
                <div className="edx-fld wide">
                  <label>{ui("content.recording")}
                    <code>{`kit[${i}].ready.audio`}</code></label>
                  <audio className="edx-player" controls preload="none"
                         src={`/api/media/${encodeURIComponent(String(ready["audio"]))}`} />
                </div>
              ) : null}
            </>
          ) : null}

          <div className="edx-formfoot wide">
            <button type="button" className="edx-act red edx-ctl" disabled={!!ed.run}
                    onClick={() => void ed.fill(kind)}>{ui("content.fillModel")}</button>
            {readyAs === "app" && typeof ready?.["app"] === "string" ? (
              <a className="edx-act" href={`/api/content/apps/${ready["app"]}.html`}
                 target="_blank" rel="noreferrer">{ui("content.openApp")}</a>
            ) : null}
            {readyAs === "blocks" && item["print"] === true ? (
              <Link className="edx-act" to={`/tisk/${lessonId}/${kind}`} target="_blank">
                {ui("content.printSheet")}
              </Link>
            ) : null}
            {readyAs === "audio" ? (
              <button type="button" className="edx-act red edx-ctl" disabled={ed.busy}
                      onClick={() => void ed.voice(kind)}>
                {ready?.["audio"] ? ui("content.voiceAgain") : ui("content.voiceIt")}
              </button>
            ) : null}
            {/* An audio material of a lesson written here has no `ready` at
                all, so there is nowhere to type the script. This is that
                nowhere becoming somewhere. */}
            {!ready && kind.startsWith("audio-") ? (
              <button type="button" className="edx-act edx-ctl"
                      onClick={() => ed.saveField(["kit", i, "ready"],
                        { as: "audio", minutes: 3, script: "" })}>
                {ui("content.prepareAudio")}
              </button>
            ) : null}
            {ready ? (
              <button type="button" className="edx-act edx-ctl"
                      onClick={() => ed.saveField(["kit", i, "ready"], undefined)}>
                {ui("content.removeReady")}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {readyAs === "blocks" && Array.isArray(ready?.["blocks"]) ? (
        <Sec title={ui("content.blocksSec")}
             action={<button type="button" className="edx-add edx-ctl"
                             onClick={() => {
                               const n = (ready["blocks"] as unknown[]).length;
                               ed.saveField(["kit", i, "ready", "blocks", n], { k: "text", body: "" });
                               ed.go({ ...ed.sel, sub: { kind: "block", path: ["kit", i, "ready", "blocks", n] } });
                             }}>+ {ui("content.addBlock")}</button>}>
          <BlocksTable ed={ed} base={["kit", i, "ready", "blocks"]}
                       blocks={ready["blocks"] as unknown[]} />
        </Sec>
      ) : null}
    </>
  );
}

function PartView({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const i = ed.sel.sub?.kind === "part" ? ed.sel.sub.i : 0;
  const part = getAt(ed.parsed, ["parts", i]);
  if (!isObject(part)) return null;
  const blocks = Array.isArray(part["blocks"]) ? part["blocks"] : [];
  return (
    <>
      <div className="edx-bar">
        <h2 className="edx-title">
          {ui("content.parts")} {i + 1}
          {part["id"] ? <> — <code>{String(part["id"])}</code></> : null}
        </h2>
      </div>
      <Sec title={ui("content.blocksSec")}>
        <BlocksTable ed={ed} base={["parts", i, "blocks"]} blocks={blocks} />
      </Sec>
    </>
  );
}

/** One block: every field it has, through the generic form — nineteen kinds
 *  written out by hand would be nineteen forms drifting from types.ts. */
function BlockView({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const path = ed.sel.sub?.kind === "block" ? ed.sel.sub.path : ["blocks", 0];
  const block = getAt(ed.parsed, path);
  if (!isObject(block)) return null;
  const parentSub = path[0] === "kit"
    ? { kind: "kit" as const, i: Number(path[1]) }
    : path[0] === "parts" ? { kind: "part" as const, i: Number(path[1]) } : undefined;
  return (
    <>
      <div className="edx-bar">
        <h2 className="edx-title">Blok — <code>{String(block["k"] ?? "")}</code></h2>
        <span className="edx-mut"><code>{asPath(path)}</code></span>
        <span className="edx-grow" />
        <button type="button" className="adm-link"
                onClick={() => {
                  ed.saveField(path, undefined);
                  ed.go({ ...ed.sel, sub: parentSub });
                }}>{ui("content.deleteBlock")}</button>
      </div>
      <div key={`${ed.ver}:${asPath(path)}`}>
        <Fields doc={block}
                onChange={(next) => {
                  if (ed.parsed) ed.setText(JSON.stringify(setAt(ed.parsed, path, next), null, 2));
                }}
                onSave={(p, v) => ed.saveField([...path, ...p], v)} />
      </div>
    </>
  );
}

/* ── the generator, shown where it was started ─────────────────────────── */

function GenRun({ ed }: { ed: Ed }) {
  const { ui, href } = useI18n();
  const run = ed.run;
  if (!run) return null;
  return (
    <section className="gen-run">
      <h4>
        {ui("content.filling")} <code>{run.kind}</code>
        {!run.done ? <span className="gen-state">{run.lines} {ui("gen.lines")}</span> : null}
        <button type="button" className="adm-link" onClick={() => ed.setRun(null)}>
          {ui("maker.close")}
        </button>
      </h4>
      {!run.done ? <div className="gen-prog" aria-hidden="true"><i /></div> : null}
      {run.err ? <p className="adm-warn">{run.err}</p> : null}
      {run.note ? <div className="gen-said">{markdown(run.note, href)}</div> : null}
      {run.code ? (
        <>
          <textarea className="ed-json" readOnly value={run.code} />
          <button type="button" className="edx-act primary edx-ctl" disabled={ed.busy || !run.done}
                  onClick={() => void ed.keepApp()}>{ui("content.keepApp")}</button>
        </>
      ) : null}
    </section>
  );
}

/* ── the column itself ─────────────────────────────────────────────────── */

export function EdMain({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const row = useMemo(() => ed.rows.find((r) => r.path === ed.sel.doc), [ed.rows, ed.sel.doc]);
  const s = ed.sel;

  let view: ReactNode;
  if (s.creating) view = <NewForm ed={ed} what={s.creating} />;
  else if (s.doc && !s.doc.startsWith("docs/")) view = <TextView ed={ed} row={row} />;
  else if (s.doc && s.sub?.kind === "kit") view = <KitView ed={ed} />;
  else if (s.doc && s.sub?.kind === "goal") view = <GoalView ed={ed} />;
  else if (s.doc && s.sub?.kind === "part") view = <PartView ed={ed} />;
  else if (s.doc && s.sub?.kind === "block") view = <BlockView ed={ed} />;
  else if (s.doc) view = <DocView ed={ed} row={row} />;
  else if (s.apps) view = <AppsView ed={ed} />;
  else if (s.pages) view = <PagesView ed={ed} />;
  else if (s.subject) view = <SubjectView ed={ed} />;
  else if (s.school) view = <SchoolView ed={ed} />;
  else view = <RootView ed={ed} />;

  return (
    <main className="edx-main">
      <Crumbs ed={ed} />
      {ed.problems.length ? (
        <div className="adm-warn">
          <strong>{ui("content.refused")}</strong>
          <ul>{ed.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
      ) : null}
      {ed.warnings.length ? (
        <div className="adm-banner">
          <strong>{ui("content.warned")}</strong>
          <ul>{ed.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      ) : null}
      {ed.msg ? <p className="adm-msg">{ed.msg}</p> : null}
      <GenRun ed={ed} />
      {view}
    </main>
  );
}
